import { resolvePhysicalTrayQuantity } from "./physicalTrayQuantity";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type { CreateOrderAmendmentLineChangeRequest, Order } from "../../services/ordersApi";
import {
  useCreateOrderAmendment,
  usePreviewOrderAmendment,
  useSubmitOrderAmendment,
} from "../../hooks/useOrders";
import type { OrderAmendmentPreview } from "../../services/ordersApi";
import { varietyPricingApi, type VarietyPricing } from "../../services/varietyPricingApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { normalizeError } from "@/shared/errors/normalizeError";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { PagedCombobox } from "@/components/ui/pagedcombobox";
import { useActiveTransportRates } from "@/features/dispatch/hooks/useTransportRates";
import { buildStructuredTransportAmendmentFields } from "./orderAmendmentTransport";
import { normalizeOrderItemEnums, toCommercialQuantity, toStoredQuantity } from "./orderItemEnums";

type PricingPickerItem = VarietyPricing & { name: string };
type TransportMode = "Pickup" | "TransportRate" | "Manual";
type PaidAmountReductionSuggestion = {
  orderItemId: string;
  productName: string;
  currentQuantity: number;
  suggestedQuantity: number;
  quantityReduction: number;
  amountReduction: number;
  unitLabel: string;
};
type PaidAmountTargetPlan = {
  amountToForfeit: number;
  remainingReduction: number;
  canApplyTarget: boolean;
  suggestions: PaidAmountReductionSuggestion[];
  guidance: string | null;
};
const REDUCE_TO_AMOUNT_PAID_REASON = "Reduce order to amount paid";
const REASON_PRESETS = [
  REDUCE_TO_AMOUNT_PAID_REASON,
  "Customer reduced quantity",
  "Customer changed order mix",
];
const PAYMENT_TARGET_TOLERANCE = 1;
const getQuantityStep = (pricingMode: number | undefined) => pricingMode === 1 ? "any" : 1;
// Keep entered/loaded tray quantities exact; do not snap to an arbitrary tray step.
const normalizeQuantity = (value: number, _pricingMode: number | undefined) => value;
const isValidQuantityIncrement = (value: number, pricingMode: number | undefined, capacity?: number | null) =>
  value === 0 || (pricingMode === 1
    ? resolvePhysicalTrayQuantity(String(value), capacity).error === null
    : Number.isSafeInteger(value) && value > 0);
const getQuantityError = (value: number, minimum: number, pricingMode: number | undefined, capacity?: number | null) => {
  if (!Number.isFinite(value)) return "Enter a quantity.";
  if (value < 0) return "Quantity cannot be negative.";
  if (value < minimum) return `Cannot be less than the fulfilled quantity (${minimum}).`;
  if (!isValidQuantityIncrement(value, pricingMode, capacity)) {
    return pricingMode === 1 ? resolvePhysicalTrayQuantity(String(value), capacity).error : "Use whole units.";
  }
  return null;
};

interface CreateOrderAmendmentDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequiresCancellation?: (suggestedReason: string) => void;
}

export const CreateOrderAmendmentDialog = ({
  order: rawOrder,
  open,
  onOpenChange,
  onRequiresCancellation,
}: CreateOrderAmendmentDialogProps) => {
  const order = useMemo(() => normalizeOrderItemEnums(rawOrder), [rawOrder]);
  const [reason, setReason] = useState("");
  const [reasonSelection, setReasonSelection] = useState("");
  const [notes, setNotes] = useState("");
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [removeItems, setRemoveItems] = useState<Record<string, boolean>>({});
  const [selectedNewProductPricing, setSelectedNewProductPricing] = useState<PricingPickerItem | null>(null);
  const [newProductQuantity, setNewProductQuantity] = useState("");
  const [newProductIsCompanySeedling, setNewProductIsCompanySeedling] = useState(true);
  const [preview, setPreview] = useState<OrderAmendmentPreview | null>(null);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [overpaymentDisposition, setOverpaymentDisposition] = useState<
    "CustomerCreditBalance" | "RefundRequested"
  >("CustomerCreditBalance");
  const [transportMode, setTransportMode] = useState<TransportMode>("Pickup");
  const [transportRateId, setTransportRateId] = useState("");
  const [manualTransportCost, setManualTransportCost] = useState(String(order.transportCost ?? 0));
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState(order.deliveryLocation ?? "");
  const [deliveryContactPerson, setDeliveryContactPerson] = useState(order.deliveryContactPerson ?? "");
  const [deliveryContactPhone, setDeliveryContactPhone] = useState(order.deliveryContactPhone ?? "");
  const [forfeitBalanceTarget, setForfeitBalanceTarget] = useState(false);

  const createMutation = useCreateOrderAmendment();
  const submitMutation = useSubmitOrderAmendment();
  const previewMutation = usePreviewOrderAmendment();
  const transportRatesQuery = useActiveTransportRates();
  const confirm = useConfirm();

  const getErrorMessage = (error: unknown, fallback: string) => {
    const envelopeMessage = (error as { envelope?: { userMessage?: string } })?.envelope?.userMessage;
    return envelopeMessage || normalizeError(error).userMessage || fallback;
  };

  const isPending = createMutation.isPending || submitMutation.isPending || previewMutation.isPending;
  const isStructuredFulfilment = order.fulfilmentMethod != null;
  const isAmendmentPickup = order.fulfilmentMethod === "Delivery" && transportMode === "Pickup";

  const formatNumber = (value: number) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
  const toDateInputValue = (value?: string | null) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const getUnitLabel = useCallback((unit: number | undefined, quantity: number) => {
    const isSingular = Number(quantity) === 1;
    switch (unit) {
      case 1:
        return isSingular ? "Kilogram" : "Kilograms";
      case 2:
        return isSingular ? "Gram" : "Grams";
      case 3:
        return isSingular ? "Liter" : "Liters";
      case 4:
        return isSingular ? "Milliliter" : "Milliliters";
      case 5:
        return isSingular ? "Piece" : "Pieces";
      case 6:
        return isSingular ? "Box" : "Boxes";
      case 7:
        return isSingular ? "Bag" : "Bags";
      case 8:
        return isSingular ? "Packet" : "Packets";
      case 9:
        return isSingular ? "Bottle" : "Bottles";
      case 10:
        return isSingular ? "Can" : "Cans";
      case 11:
        return isSingular ? "Tray" : "Trays";
      default:
        return isSingular ? "Unit" : "Units";
    }
  }, []);

  const getOrderItemUnitLabel = useCallback((item: Order["orderItems"][number], quantity = Number(item.quantity ?? 0)) => {
    if (item.pricingMode === 1) {
      return `${getUnitLabel(11, quantity)}${item.trayTypeName ? ` (${item.trayTypeName})` : ""}`;
    }

    if (item.pricingMode === 2) {
      return getUnitLabel(5, quantity);
    }

    return getUnitLabel(item.unit, quantity);
  }, [getUnitLabel]);

  const getPricingUnitLabel = useCallback((pricing: Pick<VarietyPricing, "pricingMode" | "trayTypeName"> | null | undefined, quantity = Number(newProductQuantity || 0)) => {
    if (!pricing) return "Units";

    if (pricing.pricingMode === 1) {
      return `${getUnitLabel(11, quantity)}${pricing.trayTypeName ? ` (${pricing.trayTypeName})` : ""}`;
    }

    return getUnitLabel(5, quantity);
  }, [getUnitLabel, newProductQuantity]);

  const formatPricingLabel = useCallback(
    (pricing: Pick<VarietyPricing, "cropTypeName" | "varietyName" | "pricingMode" | "trayTypeName">) =>
      `${pricing.cropTypeName}${pricing.varietyName ? ` - ${pricing.varietyName}` : ""} - ${getPricingUnitLabel(pricing)}`,
    [getPricingUnitLabel],
  );
  const isReduceToAmountPaid = reason.trim().toLowerCase() === REDUCE_TO_AMOUNT_PAID_REASON.toLowerCase();
  const reasonSelectValue = reasonSelection;

  const pricingFetchPage = useCallback(
    async ({ search, pageNumber, pageSize }: { search: string; pageNumber: number; pageSize: number }) => {
      const result = await varietyPricingApi.getVarietyPricingsPaged({
        search: search.trim() || undefined,
        pageNumber,
        pageSize,
        isActive: true,
        sortBy: "cropType",
      });

      return {
        items: result.items.map((pricing) => ({
          ...pricing,
          name: formatPricingLabel(pricing),
        })),
      };
    },
    [formatPricingLabel],
  );

  const newProductPricingId = selectedNewProductPricing?.id ?? "";
  const newProductUnitLabel = getPricingUnitLabel(selectedNewProductPricing);
  const newProductQuantityNumber = Number(newProductQuantity);
  const newProductUnitPrice = selectedNewProductPricing
    ? newProductIsCompanySeedling
      ? selectedNewProductPricing.companySeedlingPrice
      : selectedNewProductPricing.propagationServicePrice
    : null;
  const activeTransportRates = transportRatesQuery.data ?? [];
  const selectedTransportRate = activeTransportRates.find((rate) => rate.id === transportRateId);
  const formatMoney = (value: number | null | undefined) => `KES ${Number(value ?? 0).toLocaleString()}`;
  const formatSignedMoney = (value: number | null | undefined) => {
    const amount = Number(value ?? 0);
    const sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
    return `${sign}KES ${Math.abs(amount).toLocaleString()}`;
  };
  const formatDate = (value?: string | null) => {
    if (!value) return "Not set";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString();
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    const initial: Record<string, number> = {};
    for (const item of order.orderItems) {
      initial[item.id] = normalizeQuantity(
        toCommercialQuantity(order, item, Number(item.quantity ?? 0)),
        item.pricingMode,
      );
    }
    // Reset transient form state whenever a different order is opened.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuantities(initial);
    setRemoveItems({});
    setTransportMode(order.transportRateId ? "TransportRate" : Number(order.transportCost ?? 0) > 0 ? "Manual" : "Pickup");
    setTransportRateId(order.transportRateId ?? "");
    setManualTransportCost(String(order.transportCost ?? 0));
    setExpectedDeliveryDate(toDateInputValue(order.expectedDeliveryDate));
    setDeliveryLocation(order.deliveryLocation ?? "");
    setDeliveryContactPerson(order.deliveryContactPerson ?? "");
    setDeliveryContactPhone(order.deliveryContactPhone ?? "");
    setForfeitBalanceTarget(false);
    setSelectedNewProductPricing(null);
    setNewProductQuantity("");
    setNewProductIsCompanySeedling(true);
    setPreview(null);
    setPreviewDialogOpen(false);
  }, [open, order]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPreview(null);
  }, [
    deliveryContactPerson,
    deliveryContactPhone,
    deliveryLocation,
    expectedDeliveryDate,
    manualTransportCost,
    newProductIsCompanySeedling,
    newProductPricingId,
    newProductQuantity,
    overpaymentDisposition,
    quantities,
    removeItems,
    transportMode,
    transportRateId,
  ]);

  const changedLines = useMemo(() => {
    const existingChanges: Array<{ type: "line-change"; payload: CreateOrderAmendmentLineChangeRequest }> = [];

    for (const item of order.orderItems) {
        const oldQty = normalizeQuantity(
          toCommercialQuantity(order, item, Number(item.quantity ?? 0)),
          item.pricingMode,
        );
        const isRemoved = !!removeItems[item.id];
        const newQtyRaw = quantities[item.id];
        const newQty = isRemoved ? 0 : Number.isFinite(newQtyRaw) ? Number(newQtyRaw) : oldQty;

        if (newQty === oldQty) {
          continue;
        }

        existingChanges.push({
          type: "line-change",
          payload: {
            orderItemId: item.id,
            action: newQty === 0
              ? "RemoveExistingLine"
              : newQty > oldQty
                ? "IncreaseExistingLine"
                : "ReduceExistingLine",
            newQuantity: toStoredQuantity(order, item, newQty),
            remove: newQty === 0,
            notes: `${newQty > oldQty ? "Increase" : newQty === 0 ? "Remove" : "Reduce"} ${item.productName} from ${oldQty} to ${newQty}`,
          },
        });
      }

    const addQuantity = Number(newProductQuantity);
    if (newProductPricingId && Number.isFinite(addQuantity) && addQuantity > 0) {
      existingChanges.push({
        type: "line-change",
        payload: {
          action: "AddNewLine" as const,
          varietyPricingId: newProductPricingId,
          isCompanySeedling: newProductIsCompanySeedling,
          quantity: addQuantity,
          newQuantity: addQuantity,
          notes: "Add new product during amendment",
        },
      });
    }

    return existingChanges;
  }, [newProductIsCompanySeedling, newProductPricingId, newProductQuantity, order, quantities, removeItems]);

  const projection = useMemo(() => {
    const currentTotal = Number(order.totalAmount ?? 0);
    const existingGoodsTotal = order.orderItems.reduce((sum, item) => {
      const currentQty = normalizeQuantity(
        toCommercialQuantity(order, item, Number(item.quantity ?? 0)),
        item.pricingMode,
      );
      const nextQtyRaw = quantities[item.id];
      const nextQty = removeItems[item.id]
        ? 0
        : Number.isFinite(nextQtyRaw) ? Math.max(0, Number(nextQtyRaw)) : currentQty;
      const currentLineTotal = Number(item.totalAmount ?? 0);
      return sum + (currentQty > 0 ? currentLineTotal * (nextQty / currentQty) : 0);
    }, 0);
    const addLineQuantity = Number.isFinite(newProductQuantityNumber) && newProductQuantityNumber > 0
      ? newProductQuantityNumber
      : 0;
    const addLineUnitPrice = Number(newProductUnitPrice ?? 0);
    const addLineSubTotal = addLineQuantity * addLineUnitPrice;
    const addLineDiscount = addLineSubTotal * (Number(order.discountPercentage ?? 0) / 100);
    const addLineTotal = Math.max(0, addLineSubTotal - addLineDiscount);
    const projectedGoodsTotal = existingGoodsTotal + addLineTotal;

    const currentTransport = Number(order.transportCost ?? 0);
    let projectedTransport = 0;
    const currentTrayQty = order.orderItems
      .filter((item) => item.pricingMode === 1)
      .reduce((sum, item) => sum + Number(item.quantity ?? 0), 0);
    const projectedTrayQty = order.orderItems
      .filter((item) => item.pricingMode === 1)
      .reduce((sum, item) => sum + (removeItems[item.id] ? 0 : Math.max(0, Number(quantities[item.id] ?? item.quantity ?? 0))), 0);
    const addedTrayQty = selectedNewProductPricing?.pricingMode === 1 ? addLineQuantity : 0;
    const totalProjectedTrayQty = projectedTrayQty + addedTrayQty;

    if (isStructuredFulfilment) {
      projectedTransport = transportMode === "Pickup" && order.fulfilmentMethod === "Delivery"
        ? 0
        : Math.max(0, Number(manualTransportCost) || 0);
    } else if (transportMode === "TransportRate") {
      projectedTransport = Number(selectedTransportRate?.costAmount ?? 0) * totalProjectedTrayQty;
    } else if (transportMode === "Manual") {
      projectedTransport = Math.max(0, Number(manualTransportCost) || 0);
    } else if (order.transportRateId && currentTrayQty > 0) {
      projectedTransport = (currentTransport / currentTrayQty) * totalProjectedTrayQty;
    }

    const projectedTotal = projectedGoodsTotal + projectedTransport;
    const paid = Number(order.paidAmount ?? 0);
    return {
      currentTotal,
      projectedGoodsTotal,
      currentTransport,
      projectedTransport,
      projectedTotal,
      delta: projectedTotal - currentTotal,
      balance: Math.max(0, projectedTotal - paid),
      overpayment: Math.max(0, paid - projectedTotal),
      approvedPayments: paid,
      pendingOrUnapprovedPayments: 0,
      currentOutstandingBalance: Math.max(0, currentTotal - paid),
      customerCreditAmount: Math.max(0, paid - projectedTotal),
      financeReviewRequired: Math.max(0, paid - projectedTotal) > 0,
    };
  }, [
    manualTransportCost,
    newProductQuantityNumber,
    newProductUnitPrice,
    order,
    quantities,
    removeItems,
    selectedTransportRate,
    selectedNewProductPricing,
    transportMode,
    isStructuredFulfilment,
  ]);

  const hasTransportChanges = useMemo(() => {
    const originalMode: TransportMode = order.transportRateId
      ? "TransportRate"
      : Number(order.transportCost ?? 0) > 0
        ? "Manual"
        : "Pickup";

    if (isStructuredFulfilment) {
      return (order.fulfilmentMethod === "Delivery" && transportMode === "Pickup")
        || Math.max(0, Number(manualTransportCost) || 0) !== Number(order.transportCost ?? 0)
        || expectedDeliveryDate !== toDateInputValue(order.expectedDeliveryDate)
        || deliveryContactPerson.trim() !== (order.deliveryContactPerson ?? "").trim()
        || deliveryContactPhone.trim() !== (order.deliveryContactPhone ?? "").trim();
    }

    return transportMode !== originalMode
      || (transportMode === "TransportRate" && transportRateId !== (order.transportRateId ?? ""))
      || (transportMode === "Manual" && Math.max(0, Number(manualTransportCost) || 0) !== Number(order.transportCost ?? 0))
      || expectedDeliveryDate !== toDateInputValue(order.expectedDeliveryDate)
      || deliveryLocation.trim() !== (order.deliveryLocation ?? "").trim()
      || deliveryContactPerson.trim() !== (order.deliveryContactPerson ?? "").trim()
      || deliveryContactPhone.trim() !== (order.deliveryContactPhone ?? "").trim();
  }, [
    deliveryContactPerson,
    deliveryContactPhone,
    deliveryLocation,
    expectedDeliveryDate,
    manualTransportCost,
    order,
    transportMode,
    transportRateId,
    isStructuredFulfilment,
  ]);

  const isDownsizingOverpayment = projection.overpayment > 0;
  const approvedPayments = preview?.approvedPayments ?? projection.approvedPayments;
  const pendingOrUnapprovedPayments = preview?.pendingOrUnapprovedPayments ?? projection.pendingOrUnapprovedPayments;
  const amountAlreadyAllocated = preview?.amountAlreadyAllocated ?? approvedPayments;
  const paymentLimitApplies = preview?.paymentLimitApplies ?? isReduceToAmountPaid;
  const maximumAmendedOrderValue = preview?.maximumAmendedOrderValue
    ?? (paymentLimitApplies ? amountAlreadyAllocated : null);
  const canReduceToAmountPaid = preview?.canReduceToAmountPaid
    ?? approvedPayments > 0;
  const currentOutstandingBalance = preview?.currentOutstandingBalance ?? projection.currentOutstandingBalance;
  const resultingBalanceAfterAmendment = preview?.resultingBalanceAfterAmendment ?? projection.balance;
  const customerCreditAmount = preview?.customerCreditAmount ?? projection.customerCreditAmount;
  const financeReviewRequired = preview?.financeReviewRequired ?? projection.financeReviewRequired;
  const canForfeitBalanceTarget = approvedPayments > PAYMENT_TARGET_TOLERANCE
    && currentOutstandingBalance > PAYMENT_TARGET_TOLERANCE;
  const paidAmountTargetPlan = useMemo(() => {
    const targetAmount = Number(amountAlreadyAllocated ?? 0);
    const targetDifference = Math.max(0, Number(projection.projectedTotal ?? 0) - targetAmount);
    const amountToForfeit = targetDifference <= PAYMENT_TARGET_TOLERANCE ? 0 : targetDifference;
    let remainingReduction = amountToForfeit;
    const suggestions: PaidAmountReductionSuggestion[] = [];

    if (amountToForfeit <= PAYMENT_TARGET_TOLERANCE || targetAmount <= 0) {
      return {
        amountToForfeit,
        remainingReduction: 0,
        canApplyTarget: false,
        suggestions,
        guidance: amountToForfeit <= PAYMENT_TARGET_TOLERANCE
          ? "Current amended total is within the approved payment amount after rounding."
          : null,
      } satisfies PaidAmountTargetPlan;
    }

    let reducibleLineAmount = 0;

    for (const item of order.orderItems) {
      const originalQty = normalizeQuantity(
        toCommercialQuantity(order, item, Number(item.quantity ?? 0)),
        item.pricingMode,
      );
      const currentQty = removeItems[item.id]
        ? 0
        : Math.max(0, Number(quantities[item.id] ?? originalQty));
      const fulfilledQty = normalizeQuantity(
        toCommercialQuantity(order, item, Number(item.quantityFulfilled ?? 0)),
        item.pricingMode,
      );
      const reducibleQty = Math.max(0, currentQty - fulfilledQty);
      const effectiveUnitAmount = originalQty > 0
        ? Number(item.totalAmount ?? 0) / originalQty
        : Number(item.unitPrice ?? 0);
      reducibleLineAmount += Math.max(0, reducibleQty * effectiveUnitAmount);

      if (remainingReduction <= PAYMENT_TARGET_TOLERANCE || reducibleQty <= 0 || effectiveUnitAmount <= 0) {
        continue;
      }

      const quantityReduction = Math.min(reducibleQty, remainingReduction / effectiveUnitAmount);
      const suggestedQuantity = Math.max(
        fulfilledQty,
        normalizeQuantity(currentQty - quantityReduction, item.pricingMode),
      );
      const normalizedReduction = currentQty - suggestedQuantity;
      if (normalizedReduction <= 0) continue;
      const amountReduction = normalizedReduction * effectiveUnitAmount;

      suggestions.push({
        orderItemId: item.id,
        productName: item.productName,
        currentQuantity: currentQty,
        suggestedQuantity,
        quantityReduction: normalizedReduction,
        amountReduction,
        unitLabel: getOrderItemUnitLabel(item, suggestedQuantity),
      });

      remainingReduction -= amountReduction;
    }

    const projectedTransport = Number(projection.projectedTransport ?? 0);
    const guidance = remainingReduction > PAYMENT_TARGET_TOLERANCE
      ? projectedTransport > PAYMENT_TARGET_TOLERANCE && reducibleLineAmount + PAYMENT_TARGET_TOLERANCE >= amountToForfeit - projectedTransport
        ? "Transport is keeping the amended total above approved payments. Change transport to customer pickup, lower the delivery fee, or use a normal amendment."
        : "The target cannot be reached from unfulfilled quantities. Fulfilled quantities cannot be reduced through an amendment."
      : null;

    return {
      amountToForfeit,
      remainingReduction: Math.max(0, remainingReduction),
      canApplyTarget: remainingReduction <= PAYMENT_TARGET_TOLERANCE && suggestions.length > 0,
      suggestions,
      guidance,
    } satisfies PaidAmountTargetPlan;
  }, [
    getOrderItemUnitLabel,
    amountAlreadyAllocated,
    order,
    projection.projectedTotal,
    projection.projectedTransport,
    quantities,
    removeItems,
  ]);

  const handleApplyPaidAmountTarget = () => {
    if (paidAmountTargetPlan.suggestions.length === 0) {
      toast.error("No reducible order lines are available for this balance.");
      return;
    }

    if (!paidAmountTargetPlan.canApplyTarget) {
      toast.error(paidAmountTargetPlan.guidance ?? "The paid amount target cannot be reached from unfulfilled line quantities.");
      return;
    }

    if (!reason.trim()) {
      setReason(REDUCE_TO_AMOUNT_PAID_REASON);
      setReasonSelection(REDUCE_TO_AMOUNT_PAID_REASON);
    }

    setRemoveItems((prev) => {
      const next = { ...prev };
      for (const suggestion of paidAmountTargetPlan.suggestions) {
        next[suggestion.orderItemId] = suggestion.suggestedQuantity <= 0;
      }
      return next;
    });
    setQuantities((prev) => {
      const next = { ...prev };
      for (const suggestion of paidAmountTargetPlan.suggestions) {
        next[suggestion.orderItemId] = suggestion.suggestedQuantity;
      }
      return next;
    });
    toast.success("Suggested reductions applied");
  };

  const buildPayload = () => {
    const lineChanges = changedLines
      .filter((line) => line?.type === "line-change")
      .map((line) => line.payload);

    const transportFields = isStructuredFulfilment
      ? buildStructuredTransportAmendmentFields(
          order,
          transportMode === "Pickup",
          manualTransportCost,
          expectedDeliveryDate,
          deliveryContactPerson,
          deliveryContactPhone,
        )
      : {
          transportMode,
          transportRateId: transportMode === "TransportRate" ? transportRateId || null : null,
          manualTransportCost: transportMode === "Manual" ? Math.max(0, Number(manualTransportCost) || 0) : undefined,
          expectedDeliveryDate: expectedDeliveryDate || null,
          deliveryLocation: deliveryLocation.trim() || null,
          deliveryContactPerson: deliveryContactPerson.trim() || null,
          deliveryContactPhone: deliveryContactPhone.trim() || null,
        };

    return {
      expectedOrderAmendmentVersion: order.amendmentVersion ?? 0,
      reason: reason.trim(),
      notes: notes.trim() || undefined,
      overpaymentDisposition: isDownsizingOverpayment ? overpaymentDisposition : undefined,
      ...transportFields,
      lineChanges,
      lines: lineChanges,
    };
  };

  const handlePreview = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a reason for the amendment");
      return null;
    }

    if (isReduceToAmountPaid && approvedPayments <= PAYMENT_TARGET_TOLERANCE) {
      toast.error("This order has no approved payment. Use a normal amendment or cancel the order.");
      return null;
    }

    if (changedLines.length === 0 && !hasTransportChanges) {
      toast.error("Adjust a line, add a product, or change transport before previewing");
      return null;
    }

    for (const item of order.orderItems) {
      if (removeItems[item.id]) continue;
      const quantity = Number(quantities[item.id]);
      const fulfilledQuantity = normalizeQuantity(
        toCommercialQuantity(order, item, Number(item.quantityFulfilled ?? 0)),
        item.pricingMode,
      );
      const quantityError = getQuantityError(quantity, fulfilledQuantity, item.pricingMode, item.trayCapacity);
      if (quantityError) {
        toast.error(`${item.productName}: ${quantityError}`);
        return null;
      }
    }

    if (selectedNewProductPricing && newProductQuantity.trim()) {
      const quantity = Number(newProductQuantity);
      if (!isValidQuantityIncrement(quantity, selectedNewProductPricing.pricingMode, selectedNewProductPricing.trayCapacity)) {
        toast.error(selectedNewProductPricing.pricingMode === 1
          ? "New product tray quantity must produce whole seedlings for the selected tray type."
          : "New product piece quantity must be a whole number.");
        return null;
      }
    }

    try {
      const nextPreview = await previewMutation.mutateAsync({
        orderId: order.id,
        payload: buildPayload(),
      });
      setPreview(nextPreview);
      setPreviewDialogOpen(true);
      toast.success(nextPreview.requiresCancellation ? "Cancellation handoff required" : "Preview ready");
      return nextPreview;
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to preview amendment"));
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error("Please provide a reason for the amendment");
      return;
    }

    if (changedLines.length === 0 && !hasTransportChanges) {
      toast.error("Adjust a line, add a product, or change transport before submitting");
      return;
    }

    const nextPreview = await handlePreview();
    if (!nextPreview) return;

    if (nextPreview.requiresCancellation) {
      const confirmed = window.confirm("Removing all items will cancel this order. Continue to Order Cancellation?");
      if (confirmed) {
        onRequiresCancellation?.(
          nextPreview.suggestedCancellationReason ?? "Customer removed all items during order amendment."
        );
      }
      return;
    }

    const previewSummary = renderPreviewSummary(nextPreview);

    await confirm({
      title: "Submit amendment for approval?",
      description: "No live order quantities change until this amendment is approved and applied.",
      confirmLabel: "Submit for Approval",
      body: previewSummary,
      onConfirm: async () => {
        try {
          const amendment = await createMutation.mutateAsync({
            orderId: order.id,
            payload: buildPayload(),
          });

          await submitMutation.mutateAsync({
            orderId: order.id,
            amendmentId: amendment.id,
            payload: {
              notes: "Submitted from Orders quick-amend dialog",
            },
          });

          toast.success("Order amendment submitted for approval");
          onOpenChange(false);
          setReason("");
          setReasonSelection("");
          setNotes("");
        } catch (error) {
          toast.error(getErrorMessage(error, "Failed to submit amendment"));
          throw error;
        }
      },
    });
  };

  const hasPreviewTransportChange = (amendmentPreview: OrderAmendmentPreview) => {
    return Number(amendmentPreview.oldTransportCost ?? 0) !== Number(amendmentPreview.proposedTransportCost ?? 0)
      || (amendmentPreview.proposedTransportRateId ?? null) !== (order.transportRateId ?? null)
      || toDateInputValue(amendmentPreview.oldExpectedDeliveryDate) !== toDateInputValue(amendmentPreview.proposedExpectedDeliveryDate)
      || (amendmentPreview.oldDeliveryLocation ?? "").trim() !== (amendmentPreview.proposedDeliveryLocation ?? "").trim()
      || (amendmentPreview.oldDeliveryContactPerson ?? "").trim() !== (amendmentPreview.proposedDeliveryContactPerson ?? "").trim()
      || (amendmentPreview.oldDeliveryContactPhone ?? "").trim() !== (amendmentPreview.proposedDeliveryContactPhone ?? "").trim();
  };

  const renderPreviewSummary = (amendmentPreview: OrderAmendmentPreview) => {
    const transportChanged = hasPreviewTransportChange(amendmentPreview);
    const destinationLabel = amendmentPreview.proposedTransportRateId
      ? selectedTransportRate
        ? `${selectedTransportRate.destinationName}${selectedTransportRate.region ? ` - ${selectedTransportRate.region}` : ""}`
        : "Selected delivery destination"
      : isAmendmentPickup ? "Customer pickup" : "No destination rate";

    return (
      <div className="space-y-3">
        <div className="rounded-md border p-3">
          <p className="font-medium">Line changes</p>
          {amendmentPreview.lines.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No quantity or product changes.</p>
          ) : (
            <div className="mt-2 space-y-2">
              {amendmentPreview.lines.map((line, index) => {
                const item = line.orderItemId
                  ? order.orderItems.find((orderItem) => orderItem.id === line.orderItemId)
                  : null;
                const originalQuantity = item
                  ? toCommercialQuantity(order, item, line.originalQuantity)
                  : line.originalQuantity;
                const proposedQuantity = item
                  ? toCommercialQuantity(order, item, line.proposedQuantity)
                  : line.proposedQuantity;
                const quantityDifference = proposedQuantity - originalQuantity;
                const unitLabel = item
                  ? getOrderItemUnitLabel(item, proposedQuantity)
                  : getPricingUnitLabel(
                      line.varietyPricingId === selectedNewProductPricing?.id ? selectedNewProductPricing : null,
                      proposedQuantity
                    );
                const direction = quantityDifference > 0 ? "+" : "";

                return (
                  <div key={`${line.orderItemId ?? line.varietyPricingId ?? index}-${index}`} className="flex justify-between gap-3">
                    <span>
                      {line.productName}{line.variety ? ` - ${line.variety}` : ""}
                      <span className="block text-xs text-muted-foreground">{line.operation}</span>
                    </span>
                    <span className="text-right font-medium">
                      {formatNumber(originalQuantity)} {"->"} {formatNumber(proposedQuantity)} {unitLabel}
                      <span className="block text-xs text-muted-foreground">
                        {direction}{formatNumber(quantityDifference)} {unitLabel}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-md border p-3">
          <p className="font-medium">Transport changes</p>
          {transportChanged ? (
            <div className="mt-2 space-y-2 text-sm">
              <p className="flex justify-between gap-3">
                <span className="text-muted-foreground">Destination</span>
                <span className="text-right font-medium">{destinationLabel}</span>
              </p>
              <p className="flex justify-between gap-3">
                <span className="text-muted-foreground">Transport cost</span>
                <span className="text-right font-medium">
                  {formatMoney(amendmentPreview.oldTransportCost)} {"->"} {formatMoney(amendmentPreview.proposedTransportCost)}
                </span>
              </p>
              <p className="flex justify-between gap-3">
                <span className="text-muted-foreground">Delivery date</span>
                <span className="text-right font-medium">
                  {formatDate(amendmentPreview.oldExpectedDeliveryDate)} {"->"} {formatDate(amendmentPreview.proposedExpectedDeliveryDate)}
                </span>
              </p>
              <p className="flex justify-between gap-3">
                <span className="text-muted-foreground">Delivery location</span>
                <span className="text-right font-medium">
                  {amendmentPreview.oldDeliveryLocation || "Not set"} {"->"} {amendmentPreview.proposedDeliveryLocation || "Not set"}
                </span>
              </p>
              <p className="flex justify-between gap-3">
                <span className="text-muted-foreground">Contact</span>
                <span className="text-right font-medium">
                  {amendmentPreview.proposedDeliveryContactPerson || "Not set"}
                  {amendmentPreview.proposedDeliveryContactPhone ? `, ${amendmentPreview.proposedDeliveryContactPhone}` : ""}
                </span>
              </p>
            </div>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">No pickup or delivery changes.</p>
          )}
        </div>

        <div className="rounded-md border p-3 space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">New order total</span>
            <span className="font-medium">{formatMoney(amendmentPreview.proposedOrderTotal)}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Order value change</span>
            <span className="font-medium">{formatSignedMoney(amendmentPreview.totalDifference)}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Amount due after amendment</span>
            <span className="font-medium">{formatMoney(amendmentPreview.additionalAmountDue)}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Customer credit</span>
            <span className="font-medium">{formatMoney(amendmentPreview.customerCreditAmount)}</span>
          </p>
          {amendmentPreview.financeReviewRequired && (
            <p className="pt-1 text-xs font-medium text-amber-700">Finance review required</p>
          )}
        </div>

        {amendmentPreview.operationalWarnings.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-950">
            <p className="font-medium">Operational check before approval</p>
            {amendmentPreview.operationalWarnings.map((warning) => (
              <p key={warning} className="mt-1 text-xs">{warning}</p>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(96vw,1120px)] sm:max-w-6xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle>Amend Order {order.orderNumber}</DialogTitle>
          <DialogDescription>
            Increase, reduce, or remove line quantities, then submit for approval.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 px-6 py-5">
          <div className="grid gap-4 lg:grid-cols-2">
          <Field>
            <FieldLabel>Reason *</FieldLabel>
            <Select
              value={reasonSelectValue}
              onValueChange={(value) => {
                setReasonSelection(value);
                setReason(value === "custom" ? "" : value);
              }}
              disabled={isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select amendment reason" />
              </SelectTrigger>
              <SelectContent>
                {REASON_PRESETS.map((preset) => (
                  <SelectItem
                    key={preset}
                    value={preset}
                    disabled={preset === REDUCE_TO_AMOUNT_PAID_REASON && !canReduceToAmountPaid}
                  >
                    {preset}
                  </SelectItem>
                ))}
                <SelectItem value="custom">Custom reason</SelectItem>
              </SelectContent>
            </Select>
            {reasonSelection === "custom" && (
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this order being amended?"
                disabled={isPending}
                className="mt-2 min-h-16"
              />
            )}
            {!canReduceToAmountPaid && (
              <p className="text-xs text-muted-foreground">
                Reduce to amount paid is unavailable because there is no approved payment. Normal amendments remain available.
              </p>
            )}
          </Field>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional internal notes"
              disabled={isPending}
              className="min-h-24"
            />
          </Field>
          </div>

          <div className="rounded-md border p-4 space-y-4">
            <div>
              <p className="font-medium">Transport</p>
              <p className="text-xs text-muted-foreground">
                {isStructuredFulfilment
                  ? "Choose customer pickup to remove the delivery charge and destination, or keep delivery and adjust the charge."
                  : "Change pickup or delivery terms for this amendment."}
              </p>
            </div>
            {isStructuredFulfilment ? (
              <div className="grid gap-3 lg:grid-cols-3">
                {order.fulfilmentMethod === "Delivery" && (
                  <Field className="lg:col-span-3">
                    <FieldLabel>Fulfilment method</FieldLabel>
                    <Select
                      value={isAmendmentPickup ? "Pickup" : "TransportRate"}
                      onValueChange={(value: "Pickup" | "TransportRate") => {
                        setTransportMode(value);
                        setManualTransportCost(value === "Pickup" ? "0" : String(order.transportCost ?? 0));
                      }}
                      disabled={isPending}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TransportRate">FarmSmart Delivery</SelectItem>
                        <SelectItem value="Pickup">Customer pickup — no transport charge</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                {!isAmendmentPickup && (
                  <>
                    <Field>
                      <FieldLabel>Transport charge</FieldLabel>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={manualTransportCost}
                        onChange={(event) => setManualTransportCost(event.target.value)}
                        aria-label="Transport charge"
                        disabled={isPending}
                      />
                    </Field>
                    <Field className="lg:col-span-2">
                      <FieldLabel>Fulfillment / delivery destination</FieldLabel>
                      <Input
                        value={order.deliveryStopName || order.transportDestinationName || order.deliveryLocation || "Configured destination"}
                        readOnly
                        disabled
                      />
                    </Field>
                  </>
                )}
              </div>
            ) : (
              <div className="grid gap-3 lg:grid-cols-3">
              <Field>
                <FieldLabel>Mode</FieldLabel>
                <Select
                  value={transportMode}
                  onValueChange={(value: TransportMode) => {
                    setTransportMode(value);
                    if (value === "Pickup") {
                      setTransportRateId("");
                      setManualTransportCost("0");
                    } else if (value === "TransportRate") {
                      setManualTransportCost("0");
                    } else {
                      setTransportRateId("");
                    }
                  }}
                  disabled={isPending}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pickup">Customer pickup</SelectItem>
                    <SelectItem value="TransportRate">Delivery destination rate</SelectItem>
                    <SelectItem value="Manual">Manual delivery fee</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              {transportMode === "TransportRate" && (
                <Field className="lg:col-span-2">
                  <FieldLabel>Destination</FieldLabel>
                  <Select
                    value={transportRateId || "none"}
                    onValueChange={(value) => setTransportRateId(value === "none" ? "" : value)}
                    disabled={isPending || transportRatesQuery.isLoading}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={transportRatesQuery.isLoading ? "Loading destinations..." : "Select destination"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select destination</SelectItem>
                      {activeTransportRates.map((rate) => (
                        <SelectItem key={rate.id} value={rate.id}>
                          {rate.destinationName}{rate.region ? ` - ${rate.region}` : ""} - KES {rate.costAmount.toLocaleString()} / tray
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}

              {transportMode === "Manual" && (
                <Field className="lg:col-span-2">
                  <FieldLabel>Manual fee</FieldLabel>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={manualTransportCost}
                    onChange={(event) => setManualTransportCost(event.target.value)}
                    aria-label="Manual transport fee"
                    disabled={isPending}
                  />
                </Field>
              )}
              </div>
            )}

            {!isAmendmentPickup && (isStructuredFulfilment || transportMode !== "Pickup") && (
              <div className="grid gap-3 lg:grid-cols-4">
                <Field>
                  <FieldLabel>Expected delivery date</FieldLabel>
                  <Input
                    type="date"
                    value={expectedDeliveryDate}
                    onChange={(event) => setExpectedDeliveryDate(event.target.value)}
                    disabled={isPending}
                  />
                </Field>
                {!isStructuredFulfilment && (
                  <Field className="lg:col-span-3">
                    <FieldLabel>Delivery location</FieldLabel>
                    <Input
                      value={deliveryLocation}
                      onChange={(event) => setDeliveryLocation(event.target.value)}
                      placeholder="Delivery location"
                      disabled={isPending}
                    />
                  </Field>
                )}
                <Field className="lg:col-span-2">
                  <FieldLabel>Contact person</FieldLabel>
                  <Input
                    value={deliveryContactPerson}
                    onChange={(event) => setDeliveryContactPerson(event.target.value)}
                    placeholder="Contact person"
                    disabled={isPending}
                  />
                </Field>
                <Field className="lg:col-span-2">
                  <FieldLabel>Contact phone</FieldLabel>
                  <Input
                    value={deliveryContactPhone}
                    onChange={(event) => setDeliveryContactPhone(event.target.value)}
                    placeholder="Contact phone"
                    disabled={isPending}
                  />
                </Field>
              </div>
            )}
          </div>

          {isDownsizingOverpayment && (
            <Field>
              <FieldLabel>Overpayment Handling *</FieldLabel>
              <Select
                value={overpaymentDisposition}
                onValueChange={(value: "CustomerCreditBalance" | "RefundRequested") =>
                  setOverpaymentDisposition(value)
                }
                disabled={isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select handling" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CustomerCreditBalance">Transfer to Customer Wallet (Credit)</SelectItem>
                  <SelectItem value="RefundRequested">Queue Refund Request</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}

          <div className="rounded-md border p-4 text-sm">
            <div className="grid gap-x-8 gap-y-2 md:grid-cols-2">
              <div className="flex justify-between"><span>Original order total</span><span>{formatMoney(order.totalAmount)}</span></div>
              <div className="flex justify-between"><span>Approved payments</span><span>{formatMoney(approvedPayments)}</span></div>
              <div className="flex justify-between"><span>Pending or unapproved payments</span><span>{formatMoney(pendingOrUnapprovedPayments)}</span></div>
              <div className="flex justify-between"><span>Amount already allocated</span><span>{formatMoney(amountAlreadyAllocated)}</span></div>
              {paymentLimitApplies && (
                <div className="flex justify-between"><span>Payment-based amendment limit</span><span>{formatMoney(maximumAmendedOrderValue)}</span></div>
              )}
              <div className="flex justify-between"><span>Current outstanding balance</span><span>{formatMoney(currentOutstandingBalance)}</span></div>
              <div className="flex justify-between font-medium"><span>Amount due after amendment</span><span>{formatMoney(resultingBalanceAfterAmendment)}</span></div>
              <div className="flex justify-between font-medium"><span>Customer credit</span><span>{formatMoney(customerCreditAmount)}</span></div>
            </div>
            {isReduceToAmountPaid && (
              <p className="pt-2 text-xs text-muted-foreground">
                This reason caps the amended order at approved payments. Lower totals create customer credit or refund review.
              </p>
            )}
            {financeReviewRequired && (
              <p className="pt-2 text-xs font-medium text-amber-700">
                Finance review required for customer credit or refund handling.
              </p>
            )}
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-md border p-4 text-sm">
            <Checkbox
              checked={canForfeitBalanceTarget && forfeitBalanceTarget}
              onCheckedChange={(checked) => {
                const nextChecked = checked === true;
                setForfeitBalanceTarget(nextChecked);
                if (nextChecked && !reason.trim()) {
                  setReason(REDUCE_TO_AMOUNT_PAID_REASON);
                  setReasonSelection(REDUCE_TO_AMOUNT_PAID_REASON);
                }
              }}
              disabled={isPending || !canForfeitBalanceTarget}
              aria-label="Forfeit outstanding balance"
            />
            <span>
              <span className="block font-medium">Forfeit outstanding balance</span>
              {approvedPayments <= PAYMENT_TARGET_TOLERANCE ? (
                <span className="block text-xs text-muted-foreground">
                  Not available because this order has no approved payment. Use a normal amendment or cancel the order.
                </span>
              ) : currentOutstandingBalance <= PAYMENT_TARGET_TOLERANCE ? (
                <span className="block text-xs text-muted-foreground">
                  Not needed because this order has no outstanding balance.
                </span>
              ) : (
                <span className="block text-xs text-muted-foreground">
                  Reduce this order to the approved payment amount and write off the remaining balance through an amendment.
                </span>
              )}
            </span>
          </label>

          {canForfeitBalanceTarget && forfeitBalanceTarget && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="font-medium">Forfeit balance target</p>
                  {paidAmountTargetPlan.amountToForfeit > PAYMENT_TARGET_TOLERANCE ? (
                    <p className="mt-1 text-xs">
                      Reduce the amended order by {formatMoney(paidAmountTargetPlan.amountToForfeit)} to match approved payments of {formatMoney(maximumAmendedOrderValue)}.
                    </p>
                  ) : (
                    <p className="mt-1 text-xs">
                      Current amended total is within the approved payment amount after rounding.
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleApplyPaidAmountTarget}
                  disabled={isPending || !paidAmountTargetPlan.canApplyTarget}
                  className="border-amber-300 bg-white"
                >
                  Apply Target
                </Button>
              </div>

              {paidAmountTargetPlan.suggestions.length > 0 && (
                <div className="mt-3 space-y-2">
                  {paidAmountTargetPlan.suggestions.map((suggestion) => (
                    <div key={suggestion.orderItemId} className="flex flex-col gap-1 rounded-md bg-white/70 px-3 py-2 md:flex-row md:items-center md:justify-between">
                      <span className="font-medium">{suggestion.productName}</span>
                      <span className="text-right">
                        {formatNumber(suggestion.currentQuantity)} {"->"} {formatNumber(suggestion.suggestedQuantity)} {suggestion.unitLabel}
                        <span className="block text-xs text-amber-800">
                          Reduce {formatNumber(suggestion.quantityReduction)} to remove {formatMoney(suggestion.amountReduction)}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {paidAmountTargetPlan.remainingReduction > PAYMENT_TARGET_TOLERANCE && (
                <p className="mt-3 text-xs font-medium">
                  {formatMoney(paidAmountTargetPlan.remainingReduction)} cannot be covered by unfulfilled line quantities.
                </p>
              )}

              {paidAmountTargetPlan.guidance && (
                <p className="mt-2 text-xs text-amber-900">
                  {paidAmountTargetPlan.guidance}
                </p>
              )}
            </div>
          )}

          <div className="rounded-md border">
            <div className="border-b bg-muted/30 px-4 py-3">
              <p className="font-medium">Existing Lines</p>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[940px] text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-4 py-3">Product</th>
                  <th className="text-right px-4 py-3">Fulfilled</th>
                  <th className="text-right px-4 py-3">Current Qty</th>
                  <th className="text-right px-4 py-3">New Qty</th>
                  <th className="text-right px-4 py-3">Difference</th>
                  <th className="text-right px-4 py-3">Remove</th>
                </tr>
              </thead>
              <tbody>
                {order.orderItems.map((item) => {
                  const currentQty = normalizeQuantity(
                    toCommercialQuantity(order, item, Number(item.quantity ?? 0)),
                    item.pricingMode,
                  );
                  const fulfilledQty = normalizeQuantity(
                    toCommercialQuantity(order, item, Number(item.quantityFulfilled ?? 0)),
                    item.pricingMode,
                  );
                  const removed = !!removeItems[item.id];
                  const nextQty = removed ? 0 : Number(quantities[item.id] ?? currentQty);
                  const diff = nextQty - currentQty;
                  const unitLabel = getOrderItemUnitLabel(item, nextQty);
                  const quantityError = removed
                    ? null
                    : getQuantityError(nextQty, fulfilledQty, item.pricingMode, item.trayCapacity);
                  const quantityErrorId = `amendment-quantity-error-${item.id}`;

                  return (
                    <tr key={item.id} className="border-t">
                      <td className="px-4 py-3">
                        <div>{item.productName}</div>
                        <div className="text-xs text-muted-foreground">{unitLabel}</div>
                      </td>
                      <td className="px-4 py-3 text-right">{formatNumber(fulfilledQty)} {getOrderItemUnitLabel(item, fulfilledQty)}</td>
                      <td className="px-4 py-3 text-right">{formatNumber(currentQty)} {getOrderItemUnitLabel(item, currentQty)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Input
                            type="number"
                            min={fulfilledQty}
                            step={getQuantityStep(item.pricingMode)}
                            value={removed ? 0 : quantities[item.id] ?? currentQty}
                            onFocus={(event) => event.currentTarget.select()}
                            onChange={(e) => {
                              setRemoveItems((prev) => ({ ...prev, [item.id]: false }));
                              setQuantities((prev) => ({
                                ...prev,
                                [item.id]: Number(e.target.value),
                            }));
                          }}
                          disabled={isPending || removed}
                          className="min-w-36 border-2 border-primary/50 bg-background text-right font-medium shadow-sm focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/25"
                          aria-label={`New quantity in ${unitLabel}`}
                          aria-invalid={quantityError ? true : undefined}
                          aria-describedby={quantityError ? quantityErrorId : undefined}
                          />
                          <span className="min-w-28 text-left text-xs text-muted-foreground">{unitLabel}</span>
                        </div>
                        {quantityError && (
                          <p id={quantityErrorId} className="mt-1 text-right text-xs font-medium text-destructive">
                            {quantityError}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">{diff > 0 ? "+" : ""}{formatNumber(diff)} {unitLabel}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          type="button"
                          size="sm"
                          variant={removed ? "secondary" : "outline"}
                          onClick={() =>
                            setRemoveItems((prev) => ({
                              ...prev,
                              [item.id]: !prev[item.id],
                            }))
                          }
                          disabled={isPending || fulfilledQty > 0}
                          aria-label={removed ? `Undo removing ${item.productName}` : `Remove ${item.productName}`}
                        >
                          {removed ? "Undo" : "Remove"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>

          <div className="rounded-md border p-4 space-y-3">
            <div>
              <div className="font-medium">Add Product</div>
              {selectedNewProductPricing && (
                <p className="text-xs text-muted-foreground">
                  Quantity will be captured as {newProductUnitLabel}.
                </p>
              )}
            </div>
            <div className="grid gap-3 lg:grid-cols-[minmax(260px,1fr)_180px_200px]">
              <PagedCombobox<PricingPickerItem>
                title="product / variety"
                value={selectedNewProductPricing}
                onChange={(pricing) => setSelectedNewProductPricing(pricing)}
                fetchPage={pricingFetchPage}
                renderLabel={formatPricingLabel}
                pageSize={20}
                disabled={isPending}
              />
              <Input
                type="number"
                min="0"
                step={getQuantityStep(selectedNewProductPricing?.pricingMode)}
                value={newProductQuantity}
                onChange={(event) => setNewProductQuantity(event.target.value)}
                placeholder={`Qty (${newProductUnitLabel})`}
                disabled={isPending}
                aria-label={`New product quantity in ${newProductUnitLabel}`}
              />
              <Select
                value={newProductIsCompanySeedling ? "company" : "propagation"}
                onValueChange={(value) => setNewProductIsCompanySeedling(value === "company")}
                disabled={isPending}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="company">Company seedling</SelectItem>
                  <SelectItem value="propagation">Propagation service</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-md border bg-muted/20 p-4 text-sm">
            <div className="grid gap-x-8 gap-y-2 md:grid-cols-2">
            <div className="flex justify-between"><span>Amended goods</span><span>KES {(preview?.proposedGoodsTotal ?? projection.projectedGoodsTotal).toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Transport</span><span>KES {(preview?.proposedTransportCost ?? projection.projectedTransport).toLocaleString()}</span></div>
            <div className="flex justify-between font-medium"><span>Amended total</span><span>KES {(preview?.proposedOrderTotal ?? projection.projectedTotal).toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Order value change</span><span className={(preview?.totalDifference ?? projection.delta) < 0 ? "text-emerald-700" : "text-amber-700"}>{formatSignedMoney(preview?.totalDifference ?? projection.delta)}</span></div>
            <div className="flex justify-between md:col-start-2"><span>Amount due after amendment</span><span>{formatMoney(preview?.additionalAmountDue ?? projection.balance)}</span></div>
            </div>
            {(preview?.overpayment ?? projection.overpayment) > 0 && <div className="flex justify-between font-medium text-emerald-700"><span>Overpayment</span><span>KES {(preview?.overpayment ?? projection.overpayment).toLocaleString()}</span></div>}
            {preview?.requiresCancellation && <p className="pt-1 text-sm font-medium text-destructive">{preview.message}</p>}
            {preview?.validationErrors?.map((error) => <p key={error} className="pt-1 text-sm text-destructive">{error}</p>)}
            {preview?.operationalWarnings?.map((warning) => <p key={warning} className="pt-1 text-xs text-amber-700">Warning: {warning}</p>)}
            {order.transportRateId && <p className="pt-1 text-xs text-muted-foreground">Tray-based transport recalculates with amended tray quantities. The server confirms the authoritative amount.</p>}
          </div>

          <div className="sticky bottom-0 -mx-6 flex justify-end gap-2 border-t bg-background/95 px-6 py-4 backdrop-blur">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button variant="outline" onClick={handlePreview} disabled={isPending}>
              {previewMutation.isPending ? "Previewing..." : "Preview"}
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? "Submitting..." : "Submit Amendment"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Amendment Preview</DialogTitle>
          <DialogDescription>
            Review the server-calculated changes before submitting for approval.
          </DialogDescription>
        </DialogHeader>
        {preview && renderPreviewSummary(preview)}
        <DialogFooter>
          <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
